import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  Building2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  Edit, 
  Plus,
  Search,
  Filter,
  AlertCircle,
  UserCheck,
  Mail,
  Phone,
  Briefcase,
  BarChart3,
  Shield,
  UserX,
  User,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { getAgencyRiskScore, getAgencyRiskAssignments } from '../services/riskScoreService'

const AdminDashboard = ({ currentUser }) => {
  const [pendingUsers, setPendingUsers] = useState([])
  const [approvedUsers, setApprovedUsers] = useState([])
  const [agencies, setAgencies] = useState([])
  const [pendingAgencies, setPendingAgencies] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('users')
  const [allUsers, setAllUsers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserModal, setShowUserModal] = useState(false)
  const [showAgencyModal, setShowAgencyModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [newAgencyName, setNewAgencyName] = useState('')
  const [newRegistrationsCount, setNewRegistrationsCount] = useState(0)
  const [agencyRiskScores, setAgencyRiskScores] = useState([])
  const [riskScoreLoading, setRiskScoreLoading] = useState(false)

  // Fetch all data
  useEffect(() => {
    fetchAllData()
    fetchAgencyRiskScores()
    
    // Set up real-time subscription for assessment completions
    const subscription = supabase
      .channel('assessment-completions')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'assessments',
          filter: 'status=eq.completed'
        },
        (payload) => {
          console.log('Assessment completed in real-time:', payload.new)
          // Refresh risk scores when a new assessment is completed
          fetchAgencyRiskScores()
        }
      )
      .subscribe()

    // Set up subscription for agency_risk_assignments changes
    const riskSubscription = supabase
      .channel('risk-score-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agency_risk_assignments'
        },
        (payload) => {
          console.log('Risk score updated in real-time:', payload.new)
          // Refresh risk scores when risk assignments are updated
          fetchAgencyRiskScores()
        }
      )
      .subscribe()

    // Set up periodic refresh every 30 seconds
    const interval = setInterval(() => {
      console.log('Periodic refresh of agency risk scores')
      fetchAgencyRiskScores()
    }, 30000)

    // Cleanup subscriptions and interval on unmount
    return () => {
      subscription.unsubscribe()
      riskSubscription.unsubscribe()
      clearInterval(interval)
    }
  }, [])

  // Fetch agency risk scores from assessment_responses table
  const fetchAgencyRiskScores = async () => {
    try {
      setRiskScoreLoading(true)
      console.log('🔄 Fetching assessments with agency names...')

      // Fetch all completed assessments with user data including agency names
      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('assessments')
        .select(`
          id,
          title,
          description,
          score,
          risk_level,
          completed_at,
          created_at,
          status,
          user_id,
          users!inner(
            id,
            name,
            email,
            position,
            agency
          )
        `)
        .eq('status', 'completed')
        .not('users.agency', 'is', null)
        .order('completed_at', { ascending: false })

      if (!assessmentsError && assessmentsData && assessmentsData.length > 0) {
        console.log('✅ Fetched assessments data:', assessmentsData)
        
        // Format the data to include all assessments with agency information
        const formattedAssessments = assessmentsData.map(assessment => ({
          id: assessment.id,
          title: assessment.title || 'Security Assessment',
          description: assessment.description,
          score: assessment.score,
          riskLevel: assessment.risk_level,
          status: assessment.status,
          completedAt: assessment.completed_at,
          createdAt: assessment.created_at,
          userId: assessment.user_id,
          agency: assessment.users?.agency || 'Unknown Agency',
          userInfo: {
            id: assessment.users?.id,
            name: assessment.users?.name || 'Unknown User',
            email: assessment.users?.email,
            position: assessment.users?.position
          }
        }))
        
        setAgencyRiskScores(formattedAssessments)
        console.log('✅ Set assessments with agency data:', formattedAssessments)
        return
      }

      console.log('⚠️ No assessment data found, setting empty array')
      setAgencyRiskScores([])
      
    } catch (error) {
      console.error('💥 Error fetching assessments:', error)
      setAgencyRiskScores([])
    } finally {
      setRiskScoreLoading(false)
    }
  }

  const fetchAllData = async () => {
    try {
      setLoading(true)
      
      let pendingData = []
      let approvedData = []
      let agenciesData = []
      let pendingAgenciesData = []
      let allUsersData = []

      // Try to fetch pending users with boolean approved column
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('approved', false)
          .order('created_at', { ascending: false })
        if (!error) pendingData = data || []
      } catch {
        // Fallback: fetch all users and filter manually
        const { data, error } = await supabase.from('users').select('*').limit(100)
        if (!error) {
          pendingData = (data || []).filter(u => !u.approved)
          approvedData = (data || []).filter(u => u.approved === true)
        }
      }

      // Try to fetch approved users
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('approved', true)
          .order('created_at', { ascending: false })
        if (!error) approvedData = data || []
      } catch {
        // Already populated in fallback above
      }

      // Try to fetch agencies
      try {
        const { data, error } = await supabase
          .from('agencies')
          .select('*')
          .eq('is_active', true)
          .order('name')
        if (!error) agenciesData = data || []
      } catch {
        // Fallback default agencies
        agenciesData = [
          { id: 'dict', name: 'Department of Information and Communications Technology (DICT)', is_active: true },
          { id: 'npc', name: 'National Privacy Commission (NPC)', is_active: true },
          { id: 'cicc', name: 'Cybercrime Investigation and Coordinating Center (CICC)', is_active: true },
          { id: 'dnd', name: 'Department of National Defense (DND)', is_active: true },
          { id: 'afp', name: 'Armed Forces of the Philippines (AFP)', is_active: true },
          { id: 'pnp', name: 'Philippine National Police (PNP)', is_active: true },
          { id: 'nbi', name: 'National Bureau of Investigation (NBI)', is_active: true },
          { id: 'doj', name: 'Department of Justice (DOJ)', is_active: true },
          { id: 'bfp', name: 'Bureau of Fire Protection (BFP)', is_active: true },
          { id: 'op', name: 'Office of the President', is_active: true }
        ]
      }

      // Try to fetch pending agencies
      try {
        const { data, error } = await supabase
          .from('pending_agencies')
          .select(`
            *,
            users(name)
          `)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
        if (!error) pendingAgenciesData = data || []
      } catch {
        // No fallback needed
      }

      // Fetch all users for the All Users table
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .order('created_at', { ascending: false })
        if (!error) allUsersData = data || []
      } catch {
        // Fallback: combine pending and approved users
        allUsersData = [...pendingData, ...approvedData]
      }

      setPendingUsers(pendingData)
      setApprovedUsers(approvedData)
      setAgencies(agenciesData)
      setPendingAgencies(pendingAgenciesData)
      setAllUsers(allUsersData)
      setNewRegistrationsCount(pendingData.length)
      
      console.log('All users data fetched:', allUsersData.length, 'users')
      console.log('All users data sample:', allUsersData.slice(0, 2))

    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Handle user approval
  const handleApproveUser = async (userId) => {
    try {
      // Get user details for email notification
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, name')
        .eq('id', userId)
        .single()

      if (userError) throw userError

      // Update user's approval status using boolean column
      const { error } = await supabase
        .from('users')
        .update({ approved: true })
        .eq('id', userId)

      if (error) throw error

      // Send approval email notification
      try {
        const { data: emailData, error: emailError } = await supabase.functions.invoke('send-approval-notification', {
          body: {
            userId: userData.id,
            userEmail: userData.email,
            userName: userData.name,
            approvalStatus: 'approved',
            reviewedBy: currentUser.id
          }
        })
        
        if (emailError) {
          console.error('Email notification error:', emailError)
        } else {
          console.log('Approval email sent successfully:', emailData)
        }
      } catch (emailError) {
        console.error('Failed to send approval email notification:', emailError)
        // Continue with the approval process even if email fails
      }

      // Refresh data
      await fetchAllData()

      alert(`User ${userData.name} has been approved and notified via email.`)

    } catch (error) {
      console.error('Error approving user:', error)
      alert('Error approving user. Please try again.')
    }
  }

  // Handle user rejection
  const handleRejectUser = async (userId) => {
    if (!confirm('Are you sure you want to reject this user registration?')) return

    try {
      // Get user details for email notification
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, name')
        .eq('id', userId)
        .single()

      if (userError) throw userError

      // Update user's approval status using boolean column
      const { error } = await supabase
        .from('users')
        .update({ approved: false })
        .eq('id', userId)

      if (error) throw error

      // Send rejection email notification
      try {
        const { data: emailData, error: emailError } = await supabase.functions.invoke('send-approval-notification', {
          body: {
            userId: userData.id,
            userEmail: userData.email,
            userName: userData.name,
            approvalStatus: 'rejected',
            reviewedBy: currentUser.id,
            reviewNotes: 'Your registration has been reviewed and could not be approved at this time. Please contact support if you have questions.'
          }
        })
        
        if (emailError) {
          console.error('Email notification error:', emailError)
        } else {
          console.log('Rejection email sent successfully:', emailData)
        }
      } catch (emailError) {
        console.error('Failed to send rejection email notification:', emailError)
        // Continue with the rejection process even if email fails
      }

      // Refresh data
      await fetchAllData()

      alert(`User ${userData.name} has been rejected and notified via email.`)

    } catch (error) {
      console.error('Error rejecting user:', error)
      alert('Error rejecting user. Please try again.')
    }
  }

  // Handle user editing
  const handleEditUser = (user) => {
    setEditingUser({ ...user })
    setShowUserModal(true)
  }

  // Handle user update
  const handleUpdateUser = async () => {
    if (!editingUser) return

    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: editingUser.name,
          email: editingUser.email,
          agency: editingUser.agency,
          position: editingUser.position,
          department: editingUser.department,
          phone: editingUser.phone,
          role: editingUser.role,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingUser.id)

      if (error) throw error

      setShowUserModal(false)
      setEditingUser(null)
      await fetchAllData()

    } catch (error) {
      console.error('Error updating user:', error)
      alert('Error updating user. Please try again.')
    }
  }

  // Handle agency approval
  const handleApproveAgency = async (agencyId) => {
    try {
      const pendingAgency = pendingAgencies.find(a => a.id === agencyId)
      if (!pendingAgency) return

      // Create new agency
      const { data: newAgency, error: createError } = await supabase
        .from('agencies')
        .insert([{ name: pendingAgency.name, description: pendingAgency.name }])
        .select()
        .single()

      if (createError) throw createError

      // Update pending agency status
      await supabase
        .from('pending_agencies')
        .update({ status: 'approved' })
        .eq('id', agencyId)

      // Refresh data
      await fetchAllData()

    } catch (error) {
      console.error('Error approving agency:', error)
      alert('Error approving agency. Please try again.')
    }
  }

  // Handle adding new agency
  const handleAddAgency = async () => {
    if (!newAgencyName.trim()) {
      alert('Please enter an agency name')
      return
    }

    try {
      const { error } = await supabase
        .from('agencies')
        .insert([{ name: newAgencyName.trim(), description: newAgencyName.trim() }])

      if (error) throw error

      setNewAgencyName('')
      setShowAgencyModal(false)
      await fetchAllData()

    } catch (error) {
      console.error('Error adding agency:', error)
      alert('Error adding agency. Please try again.')
    }
  }

  // Filter users based on search term
  const filteredPendingUsers = pendingUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.agency.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredApprovedUsers = approvedUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.agency.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredAllUsers = allUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.agency.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Debug logging
  console.log('Debug - All Users State:', {
    allUsers: allUsers.length,
    filteredAllUsers: filteredAllUsers.length,
    searchTerm: searchTerm,
    activeTab: activeTab,
    firstUser: allUsers[0],
    isAllUsersTab: activeTab === 'allusers',
    shouldShowTable: activeTab === 'allusers' && filteredAllUsers.length > 0,
    filteredAllUsersData: filteredAllUsers.slice(0, 2) // Show first 2 filtered users
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gov-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-600 mx-auto mb-4"></div>
          <p className="text-gov-600">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gov-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gov-800 mb-2">Admin Dashboard</h1>
            <p className="text-gov-600">Manage user registrations, approvals, and agencies</p>
          </div>
          <div className="flex space-x-3">
            <a
              href="/simple-approval-test"
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Mail className="h-4 w-4 mr-2" />
              Quick Test
            </a>
            <a
              href="/test-approval-workflow"
              className="flex items-center px-4 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 transition-colors"
            >
              <Mail className="h-4 w-4 mr-2" />
              Full Workflow Test
            </a>
          </div>
        </div>

        {/* Enhanced Approval Status Widget */}
        <div className="bg-gradient-to-r from-gov-50 to-cyber-50 rounded-xl shadow-lg p-8 mb-8 border border-gov-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gov-800 flex items-center">
                <Shield className="h-8 w-8 mr-3 text-cyber-600" />
                User Approval Status Dashboard
              </h2>
              <p className="text-gov-600 mt-1">Monitor and manage user registrations with approval workflow</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{pendingUsers.length}</div>
                <div className="text-sm text-gov-500">Pending</div>
              </div>
              <div className="w-px h-12 bg-gov-300"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{approvedUsers.length}</div>
                <div className="text-sm text-gov-500">Approved</div>
              </div>
              <div className="w-px h-12 bg-gov-300"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{agencies.length}</div>
                <div className="text-sm text-gov-500">Agencies</div>
              </div>
            </div>
          </div>
          
          {/* Quick Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('users')}
              className="flex items-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors"
            >
              <Clock className="h-4 w-4 mr-2" />
              Review Pending ({pendingUsers.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className="flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              View Approved ({approvedUsers.length})
            </button>
            <button
              onClick={() => setShowAgencyModal(true)}
              className="flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Add Agency
            </button>
          </div>
        </div>

        {/* Enhanced Approval Status Widget */}
        <div className="bg-white rounded-xl shadow-lg border border-gov-200 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-gov-600 to-gov-700 px-6 py-4">
             <h2 className="text-xl font-bold text-white flex items-center">
               <BarChart3 className="h-6 w-6 mr-3" />
               Approval Status Dashboard
             </h2>
             <p className="text-gov-100 mt-1">Monitor user approvals and system statistics</p>
           </div>
           
           {/* Quick Summary */}
           <div className="px-6 py-4 bg-gradient-to-r from-gov-50 to-gov-100 border-b border-gov-200">
             <div className="flex items-center justify-between">
               <div className="flex items-center space-x-6">
                 <div className="flex items-center">
                   <div className="h-3 w-3 bg-orange-500 rounded-full mr-2"></div>
                   <span className="text-sm font-medium text-gov-700">
                     {pendingUsers.length} pending approvals
                   </span>
                 </div>
                 <div className="flex items-center">
                   <div className="h-3 w-3 bg-green-500 rounded-full mr-2"></div>
                   <span className="text-sm font-medium text-gov-700">
                     {approvedUsers.length} approved users
                   </span>
                 </div>
                 <div className="flex items-center">
                   <div className="h-3 w-3 bg-gov-600 rounded-full mr-2"></div>
                   <span className="text-sm font-medium text-gov-700">
                     {((approvedUsers.length / (pendingUsers.length + approvedUsers.length)) * 100 || 0).toFixed(1)}% approval rate
                   </span>
                 </div>
               </div>
               <div className="text-sm text-gov-600">
                 Total Users: {pendingUsers.length + approvedUsers.length}
               </div>
             </div>
           </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow p-6 border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">Pending Users</p>
                  <p className="text-3xl font-bold text-orange-700">{pendingUsers.length}</p>
                </div>
                <div className="bg-orange-200 p-3 rounded-full">
                  <UserX className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setActiveTab('users')}
                  className="w-full bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-700 transition-colors font-medium"
                >
                  Review Pending Users
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Approved Users</p>
                  <p className="text-3xl font-bold text-green-700">{approvedUsers.length}</p>
                </div>
                <div className="bg-green-200 p-3 rounded-full">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setActiveTab('users')}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition-colors font-medium"
                >
                  View Approved Users
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow p-6 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Active Agencies</p>
                  <p className="text-3xl font-bold text-blue-700">{agencies.length}</p>
                </div>
                <div className="bg-blue-200 p-3 rounded-full">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setActiveTab('agencies')}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors font-medium"
                >
                  Manage Agencies
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow p-6 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-medium">Pending Agencies</p>
                  <p className="text-3xl font-bold text-purple-700">{pendingAgencies.length}</p>
                </div>
                <div className="bg-purple-200 p-3 rounded-full">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setActiveTab('agencies')}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 transition-colors font-medium"
                >
                  Review Agencies
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Assessments with Agency Names Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-600" />
              Assessments by Agency
            </h2>
            <button
              onClick={fetchAgencyRiskScores}
              disabled={riskScoreLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {riskScoreLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {riskScoreLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading assessments...</span>
            </div>
          ) : agencyRiskScores.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No assessments available yet</p>
              <p className="text-sm">Completed assessments will appear here with their agency information</p>
            </div>
          ) : (
            <div className="space-y-4">
              {agencyRiskScores.map((assessment, index) => {
                const getRiskColor = (riskScore) => {
                  if (riskScore >= 80) return 'border-red-200 bg-red-50'
                  if (riskScore >= 60) return 'border-yellow-200 bg-yellow-50'
                  return 'border-green-200 bg-green-50'
                }

                const getRiskBadgeColor = (riskLevel) => {
                  if (riskLevel === 'high') return 'bg-red-100 text-red-800 border-red-200'
                  if (riskLevel === 'medium') return 'bg-yellow-100 text-yellow-800 border-yellow-200'
                  return 'bg-green-100 text-green-800 border-green-200'
                }

                const getScoreBadgeColor = (score) => {
                  if (score >= 80) return 'bg-green-100 text-green-800 border-green-200'
                  if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-200'
                  return 'bg-red-100 text-red-800 border-red-200'
                }

                return (
                  <motion.div
                    key={assessment.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-6 rounded-lg border-2 ${getRiskColor(assessment.score)} hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{assessment.title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getScoreBadgeColor(assessment.score)}`}>
                            Score: {assessment.score}%
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRiskBadgeColor(assessment.riskLevel)}`}>
                            {assessment.riskLevel?.toUpperCase() || 'UNKNOWN'} RISK
                          </span>
                        </div>
                        <div className="text-lg font-medium text-blue-600 mb-1">
                          🏢 {assessment.agency}
                        </div>
                        {assessment.description && (
                          <p className="text-sm text-gray-600 mb-3">{assessment.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Completed by:</span>
                          <span className="text-gray-900">{assessment.userInfo.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Email:</span>
                          <span className="text-gray-600">{assessment.userInfo.email}</span>
                        </div>
                        {assessment.userInfo.position && (
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-700">Position:</span>
                            <span className="text-gray-600">{assessment.userInfo.position}</span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Completed:</span>
                          <span className="text-gray-600">
                            {new Date(assessment.completedAt).toLocaleDateString()} at {new Date(assessment.completedAt).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">Assessment ID:</span>
                          <span className="text-gray-500 font-mono text-xs">{assessment.id}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-700">User ID:</span>
                          <span className="text-gray-500 font-mono text-xs">{assessment.userId}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gov-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm relative ${
                  activeTab === 'users'
                    ? 'border-cyber-500 text-cyber-600'
                    : 'border-transparent text-gov-500 hover:text-gov-700 hover:border-gov-300'
                }`}
              >
                User Management
                {newRegistrationsCount > 0 && (
                  <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {newRegistrationsCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  console.log('All Users tab clicked, current activeTab:', activeTab);
                  setActiveTab('allusers');
                  console.log('All Users tab clicked, new activeTab will be: allusers');
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm relative ${
                  activeTab === 'allusers'
                    ? 'border-cyber-500 text-cyber-600'
                    : 'border-transparent text-gov-500 hover:text-gov-700 hover:border-gov-300'
                }`}
              >
                All Users
                {activeTab === 'allusers' && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-cyber-500"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('agencies')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'agencies'
                    ? 'border-cyber-500 text-cyber-600'
                    : 'border-transparent text-gov-500 hover:text-gov-700 hover:border-gov-300'
                }`}
              >
                Agency Management
              </button>
            </nav>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gov-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or agency..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
              />
            </div>
            <button className="flex items-center space-x-2 px-4 py-2 border border-gov-300 rounded-lg hover:bg-gov-50">
              <Filter className="h-4 w-4" />
              <span>Filter</span>
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Enhanced Pending Users Widget */}
            <div className="bg-white rounded-xl shadow-lg border border-yellow-200">
              <div className="px-6 py-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-b border-yellow-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg mr-3">
                      <Clock className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-yellow-800">Pending User Approvals</h2>
                      <p className="text-sm text-yellow-600">Users awaiting approval to access the system</p>
                    </div>
                  </div>
                  <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                    {pendingUsers.length} Pending
                  </div>
                </div>
              </div>
              <div className="divide-y divide-yellow-100">
                {filteredPendingUsers.length === 0 ? (
                  <div className="px-6 py-12 text-center bg-yellow-50">
                    <Clock className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                    <p className="text-yellow-700 font-medium">No pending user registrations</p>
                    <p className="text-yellow-600 text-sm mt-1">All caught up! No users awaiting approval.</p>
                  </div>
                ) : (
                  filteredPendingUsers.map((user) => (
                    <div key={user.id} className="px-6 py-4 flex items-center justify-between hover:bg-yellow-50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center border-2 border-yellow-200">
                          <User className="h-6 w-6 text-yellow-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gov-900 text-lg">{user.name}</h3>
                          <p className="text-sm text-gov-600 font-medium">{user.email}</p>
                          <div className="flex items-center space-x-6 mt-2 text-sm text-gov-500">
                            <span className="flex items-center bg-white px-2 py-1 rounded border">
                              <Briefcase className="h-4 w-4 mr-2 text-yellow-600" />
                              {user.agency}
                            </span>
                            <span className="flex items-center bg-white px-2 py-1 rounded border">
                              <Mail className="h-4 w-4 mr-2 text-yellow-600" />
                              {user.position}
                            </span>
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                              Pending Review
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleApproveUser(user.id)}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                        >
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">Approve</span>
                        </button>
                        <button
                          onClick={() => handleRejectUser(user.id)}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                        >
                          <XCircle className="h-5 w-5" />
                          <span className="font-medium">Reject</span>
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="flex items-center space-x-2 px-4 py-2 bg-gov-600 text-white rounded-lg hover:bg-gov-700 transition-colors shadow-sm"
                        >
                          <Edit className="h-5 w-5" />
                          <span className="font-medium">Edit</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Approved Users */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gov-200">
                <h2 className="text-lg font-semibold text-gov-800">Approved Users</h2>
              </div>
              <div className="divide-y divide-gov-200">
                {filteredApprovedUsers.length === 0 ? (
                  <div className="px-6 py-8 text-center">
                    <CheckCircle className="h-12 w-12 text-gov-400 mx-auto mb-4" />
                    <p className="text-gov-600">No approved users</p>
                  </div>
                ) : (
                  filteredApprovedUsers.map((user) => (
                    <div key={user.id} className="px-6 py-4 flex items-center justify-between hover:bg-green-50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center shadow-sm">
                          <UserCheck className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gov-900 text-lg">{user.name}</h3>
                          <p className="text-sm text-gov-600 font-medium">{user.email}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gov-500">
                            <span className="flex items-center bg-gov-50 px-2 py-1 rounded">
                              <Briefcase className="h-3 w-3 mr-1" />
                              {user.agency}
                            </span>
                            <span className="flex items-center bg-gov-50 px-2 py-1 rounded">
                              <Mail className="h-3 w-3 mr-1" />
                              {user.position}
                            </span>
                            <span className="flex items-center bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approved
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-gov-600 to-gov-700 text-white rounded-lg hover:from-gov-700 hover:to-gov-800 transition-all duration-200 shadow-sm"
                        >
                          <Edit className="h-4 w-4" />
                          <span className="font-medium">Edit</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'agencies' && (
          <div className="space-y-6">
            {/* Add New Agency */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gov-800">Add New Agency</h2>
                <button
                  onClick={() => setShowAgencyModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-cyber-600 text-white rounded hover:bg-cyber-700"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Agency</span>
                </button>
              </div>
            </div>

            {/* Pending Agencies */}
            {pendingAgencies.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gov-200">
                  <h2 className="text-lg font-semibold text-gov-800">Pending Agency Requests</h2>
                </div>
                <div className="divide-y divide-gov-200">
                  {pendingAgencies.map((agency) => (
                    <div key={agency.id} className="px-6 py-4 flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gov-900">{agency.name}</h3>
                        <p className="text-sm text-gov-600">
                          Suggested by: {agency.users?.name || 'Unknown User'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleApproveAgency(agency.id)}
                          className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span>Approve</span>
                        </button>
                        <button className="flex items-center space-x-1 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">
                          <XCircle className="h-4 w-4" />
                          <span>Reject</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Agencies */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gov-200">
                <h2 className="text-lg font-semibold text-gov-800">Active Agencies</h2>
              </div>
              <div className="divide-y divide-gov-200">
                {agencies.length === 0 ? (
                  <div className="px-6 py-8 text-center">
                    <Building2 className="h-12 w-12 text-gov-400 mx-auto mb-4" />
                    <p className="text-gov-600">No agencies found</p>
                  </div>
                ) : (
                  agencies.map((agency) => (
                    <div key={agency.id} className="px-6 py-4 flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gov-900">{agency.name}</h3>
                        <p className="text-sm text-gov-600">{agency.description}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="px-3 py-1 text-gov-600 hover:text-gov-800">
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'allusers' && (
          <div className="space-y-6">
            {console.log('All Users tab is active, rendering content...', 'activeTab:', activeTab, 'allUsers.length:', allUsers.length, 'filteredAllUsers.length:', filteredAllUsers.length)}
            {/* All Users Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gov-200 bg-gradient-to-r from-gov-50 to-gov-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gov-800 flex items-center">
                        <Users className="h-5 w-5 mr-2 text-gov-600" />
                        All System Users
                      </h2>
                      <p className="text-sm text-gov-600 mt-1">Complete list of all registered users with approval actions</p>
                    </div>
                    <div className="bg-gov-100 text-gov-800 px-3 py-1 rounded-full text-sm font-medium">
                      {filteredAllUsers.length} {searchTerm ? 'Filtered' : 'Total'} Users
                    </div>
                  </div>
                  {/* Debug info */}
                  <div className="mt-2 text-xs text-gray-500">
                    Debug: allUsers={allUsers.length}, filteredAllUsers={filteredAllUsers.length}, searchTerm="{searchTerm}"
                  </div>
                  {allUsers.length > 0 && (
                    <div className="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
                      <strong>Sample user data:</strong> {JSON.stringify(allUsers[0], null, 2).substring(0, 200)}...
                    </div>
                  )}
                </div>
              
              <div className="overflow-x-auto">
                {(() => {
                  try {
                    return (
                      <table className="min-w-full divide-y divide-gov-200">
                  <thead className="bg-gov-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                        User Information
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                        Contact Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                        Agency & Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                        Registration Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gov-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gov-200">
                    {filteredAllUsers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center">
                          <Users className="h-12 w-12 text-gov-400 mx-auto mb-4" />
                          <p className="text-gov-600 font-medium">No users found</p>
                          <p className="text-gov-500 text-sm mt-1">
                            {searchTerm ? `No users match your search for "${searchTerm}"` : 'No users have registered in the system yet.'}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredAllUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gov-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 bg-gov-100 rounded-full flex items-center justify-center mr-3">
                                <User className="h-5 w-5 text-gov-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gov-900">{user.name}</div>
                                <div className="text-sm text-gov-500">{user.user_type || 'Standard User'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gov-900">{user.email}</div>
                            {user.phone && (
                              <div className="text-sm text-gov-500">{user.phone}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gov-900">{user.agency || 'N/A'}</div>
                            <div className="text-sm text-gov-500">{user.position || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gov-900">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                            </div>
                            <div className="text-sm text-gov-500">
                              {user.created_at ? new Date(user.created_at).toLocaleTimeString() : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {user.approved === true ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approved
                              </span>
                            ) : user.approved === false ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" />
                                Rejected
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-2">
                              {user.approved !== true && (
                                <button
                                  onClick={() => handleApproveUser(user.id)}
                                  className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors"
                                  title="Approve User"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              )}
                              {user.approved === true && (
                                <button
                                  onClick={() => handleRejectUser(user.id)}
                                  className="inline-flex items-center justify-center w-8 h-8 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                                  title="Reject User"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleEditUser(user)}
                                className="inline-flex items-center justify-center w-8 h-8 bg-gov-100 text-gov-600 rounded-full hover:bg-gov-200 transition-colors"
                                title="Edit User"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                      );
                    } catch (error) {
                      console.error('Error rendering All Users table:', error);
                      return (
                        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                          <h3 className="text-red-800 font-medium mb-2">Error rendering table</h3>
                          <p className="text-red-600 text-sm">{error.message}</p>
                          <pre className="text-xs text-red-500 mt-2 overflow-auto">{error.stack}</pre>
                        </div>
                      );
                    }
                  })()
                }
              </div>
            </div>
          </div>
        )}

        {/* User Edit Modal */}
        {showUserModal && editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gov-800 mb-4">Edit User</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingUser.name || ''}
                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gov-300 rounded focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gov-300 rounded focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-1">Agency</label>
                  <input
                    type="text"
                    value={editingUser.agency || ''}
                    onChange={(e) => setEditingUser({...editingUser, agency: e.target.value})}
                    className="w-full px-3 py-2 border border-gov-300 rounded focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-1">Position</label>
                  <input
                    type="text"
                    value={editingUser.position || ''}
                    onChange={(e) => setEditingUser({...editingUser, position: e.target.value})}
                    className="w-full px-3 py-2 border border-gov-300 rounded focus:outline-none focus:ring-2 focus:ring-cyber-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="px-4 py-2 text-gov-600 hover:text-gov-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  className="px-4 py-2 bg-cyber-600 text-white rounded hover:bg-cyber-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Agency Modal */}
        {showAgencyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gov-800 mb-4">Add New Agency</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gov-700 mb-1">Agency Name</label>
                  <input
                    type="text"
                    value={newAgencyName}
                    onChange={(e) => setNewAgencyName(e.target.value)}
                    className="w-full px-3 py-2 border border-gov-300 rounded focus:outline-none focus:ring-2 focus:ring-cyber-500"
                    placeholder="Enter agency name"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAgencyModal(false)}
                  className="px-4 py-2 text-gov-600 hover:text-gov-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAgency}
                  className="px-4 py-2 bg-cyber-600 text-white rounded hover:bg-cyber-700"
                >
                  Add Agency
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard